import React, { useEffect } from "react";
import { Card, CardBody, Row, Col, Input, Label, FormGroup } from "reactstrap";
import { StepComponentProps } from "../types/proposalForm.types";

const Step7HealthDetails: React.FC<StepComponentProps> = ({
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  setFieldValue,
}) => {
  // Calculate BMI when height or weight changes (height can be in cm or ft format; weight in kg)
  useEffect(() => {
    const heightVal = String(values.height || "").trim();
    const weightVal = String(values.weight || "").replace(/\s*kg\s*/i, "").trim();
    const height = parseFloat(heightVal);
    const weight = parseFloat(weightVal);

    if (height > 0 && weight > 0) {
      // Assume height in cm if numeric and > 2, else treat as meters; weight as kg
      const heightM = height > 10 ? height / 100 : height;
      const bmi = weight / (heightM * heightM);
      setFieldValue("bmi", bmi.toFixed(2));
    } else {
      setFieldValue("bmi", "");
    }
  }, [values.height, values.weight, setFieldValue]);

  const renderInput = (
    name: keyof typeof values,
    label: string,
    type: "text" | "number" | "date" | "email" | "tel" = "text",
    placeholder?: string,
    required: boolean = false,
    readOnly: boolean = false
  ) => (
    <FormGroup className="form-group">
      <Label>
        {label}
        {required && <span className="text-danger">*</span>}
      </Label>
      <Input
        type={type}
        name={name as string}
        value={String(values[name] || "")}
        onChange={handleChange}
        onBlur={handleBlur}
        invalid={!!(touched[name] && errors[name])}
        placeholder={placeholder}
        className="form-control"
        readOnly={readOnly}
      />
      {touched[name] && errors[name] && (
        <div className="invalid-feedback">{errors[name]}</div>
      )}
    </FormGroup>
  );

  const renderSelect = (
    name: keyof typeof values,
    label: string,
    options: Array<{ value: string; label: string }>,
    required: boolean = false
  ) => (
    <FormGroup className="form-group">
      <Label>
        {label}
        {required && <span className="text-danger">*</span>}
      </Label>
      <Input
        type="select"
        name={name as string}
        value={String(values[name] || "")}
        onChange={handleChange}
        onBlur={handleBlur}
        invalid={!!(touched[name] && errors[name])}
        className="form-control"
      >
        <option value="">Select</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Input>
      {touched[name] && errors[name] && (
        <div className="invalid-feedback">{errors[name]}</div>
      )}
    </FormGroup>
  );

  const renderTextarea = (
    name: keyof typeof values,
    label: string,
    placeholder?: string,
    required: boolean = false
  ) => (
    <FormGroup className="form-group">
      <Label>
        {label}
        {required && <span className="text-danger">*</span>}
      </Label>
      <Input
        type="textarea"
        name={name as string}
        value={String(values[name] || "")}
        onChange={handleChange}
        onBlur={handleBlur}
        invalid={!!(touched[name] && errors[name])}
        placeholder={placeholder}
        className="form-control"
        rows={4}
      />
      {touched[name] && errors[name] && (
        <div className="invalid-feedback">{errors[name]}</div>
      )}
    </FormGroup>
  );

  const renderDateInput = (
    name: keyof typeof values,
    label: string,
    required: boolean = false
  ) => (
    <FormGroup className="form-group">
      <Label>
        {label}
        {required && <span className="text-danger">*</span>}
      </Label>
      <div className="date-input-wrapper">
        <Input
          type="date"
          name={name as string}
          value={String(values[name] || "")}
          onChange={handleChange}
          onBlur={handleBlur}
          invalid={!!(touched[name] && errors[name])}
          className="form-control"
          id={`date-${name}`}
        />
        <i 
          className="ri-calendar-line calendar-icon"
          onClick={() => {
            const input = document.getElementById(`date-${name}`) as HTMLInputElement;
            if (input) {
              if (input.showPicker) {
                input.showPicker();
              } else {
                input.click();
              }
            }
          }}
        />
      </div>
      {touched[name] && errors[name] && (
        <div className="invalid-feedback">{errors[name]}</div>
      )}
    </FormGroup>
  );

  const smokingDrinkingHabits = String(values.smoking_drinking_habits || "");

  return (
    <>
      {/* Section 1: Health & Medical Information */}
      <Card className="form-card shadow-sm border-0">
        <CardBody>
          <h5 className="form-title">Health & Medical Information</h5>
          <p className="form-subtitle">
            Your health information helps us provide accurate coverage and pricing.
          </p>

          {/* Confidentiality Banner */}
          <div className="confidentiality-banner">
            <div className="confidentiality-content">
              <strong>Confidentiality Assured</strong>
              <p>All medical information is kept strictly confidential and will only be used for underwriting purposes.</p>
            </div>
          </div>

          <Row style={{ marginTop: "1.5rem" }}>
            <Col md={4}>
              {renderInput("height", "Height (e.g. 5.6 or cm)", "text", "e.g., 5.6 or 175", true)}
            </Col>
            <Col md={4}>
              {renderInput("weight", "Weight (e.g. 70 kg)", "text", "e.g., 70 kg", true)}
            </Col>
            <Col md={4}>
              {renderInput("bmi", "BMI", "text", "Auto calculated", false, true)}
            </Col>
          </Row>

          {/* Smoking/Drinking Habits */}
          <div className="gender-section" style={{ marginTop: "1.5rem" }}>
            <Label className="gender-label">
              Do you have smoking or Drinking alcohol habits<span className="text-danger">*</span>
            </Label>
            <div className="gender-options">
              {(["Yes", "No"] as const).map((option) => (
                <div key={option} className="gender-option">
                  <input
                    type="radio"
                    id={`habits-${option}`}
                    name="smoking_drinking_habits"
                    value={option}
                    checked={smokingDrinkingHabits === option}
                    onChange={(e) => setFieldValue("smoking_drinking_habits", e.target.value)}
                    className="gender-radio"
                  />
                  <label htmlFor={`habits-${option}`} className="gender-label-option">
                    {option}
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Ex-smoker Date - Conditional */}
          {smokingDrinkingHabits === "Yes" && (
            <Row style={{ marginTop: "1rem" }}>
              <Col md={6}>
                {renderDateInput(
                  "smokingstop_date",
                  "If you're an ex-smoker, please mention the approximate date when you stopped smoking",
                  false
                )}
              </Col>
            </Row>
          )}
        </CardBody>
      </Card>

      {/* Section 2: Medical History */}
      <Card className="form-card shadow-sm border-0" style={{ marginTop: "2rem" }}>
        <CardBody>
          <h5 className="form-title">Medical History</h5>
          
          <Label style={{ marginBottom: "1rem", display: "block" }}>
            Please indicate if you have or have had any medical conditions (e.g. diabetes, heart disease, asthma). If none, enter "na".
          </Label>

          <Row>
            <Col md={12}>
              {renderTextarea(
                "medicalhistory",
                "Medical History",
                "e.g. Diabetes, Heart disease or na",
                false
              )}
            </Col>
            <Col md={12}>
              {renderTextarea(
                "medical_his_detail",
                "Medical History Details",
                "For each condition, please provide diagnosis date, severity, treatment taken, and current status",
                false
              )}
            </Col>
            <Col md={6}>
              {renderDateInput("dateof_diagnosis", "Date of Diagnosis", false)}
            </Col>
            <Col md={6}>
              {renderInput(
                "treatment_status",
                "Treatment Status",
                "text",
                "e.g. Ongoing",
                false
              )}
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* Section 3: Family Medical History */}
      <Card className="form-card shadow-sm border-0" style={{ marginTop: "2rem" }}>
        <CardBody>
          <h5 className="form-title">Family Medical History</h5>
          <p className="form-subtitle">
            Information about immediate family members health conditions.
          </p>

          <Row>
            <Col md={12}>
              {renderTextarea(
                "family_medical_history",
                "Family Medical History",
                "e.g., Father: Diabetes (Age 50), Mother: Hypertension (Age 45)",
                false
              )}
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* Section 4: Additional Information (For Females Only) */}
      <Card className="form-card shadow-sm border-0" style={{ marginTop: "2rem" }}>
        <CardBody>
          <h5 className="form-title">Additional Information (For Females Only)</h5>
          
          <div className="gender-section" style={{ marginTop: "1rem" }}>
            <Label className="gender-label">
              Pregnancy Status Ongoing?
            </Label>
            <div className="gender-options">
              {([{ value: 0, label: "No" }, { value: 1, label: "Yes" }] as const).map(({ value, label }) => (
                <div key={value} className="gender-option">
                  <input
                    type="radio"
                    id={`pregnancy-${value}`}
                    name="is_preganant"
                    value={String(value)}
                    checked={Number(values.is_preganant) === value}
                    onChange={() => setFieldValue("is_preganant", value)}
                    className="gender-radio"
                  />
                  <label htmlFor={`pregnancy-${value}`} className="gender-label-option">
                    {label}
                  </label>
                </div>
              ))}
            </div>
          </div>
        </CardBody>
      </Card>
    </>
  );
};

export default Step7HealthDetails;
